import {
    AforoZone,
    AforoAreaId,
    AFORO_DAY_CURVE,
    AFORO_HORARIO,
    AFORO_HORA_EN_VIVO,
    AFORO_EVACUACION,
    AFORO_FOCUS_SPACING,
} from "./aforoConfig";
import { aforoReservations } from "./aforoReservations";
import {
    EvacuationPosition,
    EvacuationResult,
    Punto,
    evacuationPositions,
    formatSeconds,
    simulateEvacuation,
} from "./aforoEvacuation";

// Simulador de aforo: única fuente de verdad de la ocupación del edificio.
//
// Genera perfiles diarios DETERMINISTAS por (fecha, zona): la misma fecha
// produce siempre los mismos datos, lo que permite filtrar por día, mostrar
// la "foto" de una hora sobre el heatmap, prever fechas futuras y generar
// informes coherentes con las gráficas.
//
// Modos:
//  - "snapshot": fecha + hora fijas (el análisis por horas).
//  - "replay":   reloj acelerado que recorre el día en bucle.
//
// Es "pull-based": no tiene timer propio, el estado se calcula al consultarlo
// (el HeatmapAnimator y los labels ya marcan la cadencia).

//  - "evacuacion": la foto actual se vacía siguiendo la simulación de
//    evacuación (aforoEvacuation.ts), acelerada.
export type AforoMode = "snapshot" | "replay" | "evacuacion";

export interface AforoZoneState {
    zone: AforoZone;
    personas: number;
    ratio: number;
}

export interface AforoProfilePoint {
    hour: number;
    personas: number;
    ratio: number;
}

interface ZoneRuntime {
    current: number; // personas (con decimales para suavizar)
    lastMs: number;
}

const REPLAY_START_HOUR = AFORO_HORARIO.inicio;
const REPLAY_END_HOUR = AFORO_HORARIO.fin;
const REPLAY_SPEED = 600; // 1 s real = 10 min simulados (día completo ≈ 90 s)
const PROFILE_STEP_HOURS = 0.5;
// Constante de tiempo del suavizado (segundos reales para recorrer ~63% del
// salto hacia el objetivo).
const SMOOTHING_SECONDS = 2.5;

// ---- RNG determinista sembrado por texto (fecha|zona) ----
// Operaciones bit a bit intencionadas: son la base del hash/PRNG.
/* eslint-disable no-bitwise */
const hashSeed = (text: string): number => {
    let h = 1779033703 ^ text.length;
    for (let i = 0; i < text.length; i += 1) {
        h = Math.imul(h ^ text.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return h >>> 0;
};

const mulberry32 = (seed: number): (() => number) => {
    let a = seed;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};
/* eslint-enable no-bitwise */

const interpolateCurve = (hour: number): number => {
    const curve = AFORO_DAY_CURVE;
    if (hour <= curve[0][0]) return curve[0][1];
    if (hour >= curve[curve.length - 1][0]) return curve[curve.length - 1][1];
    for (let i = 1; i < curve.length; i += 1) {
        const [h1, v1] = curve[i];
        if (hour <= h1) {
            const [h0, v0] = curve[i - 1];
            const t = (hour - h0) / (h1 - h0);
            return v0 + (v1 - v0) * t;
        }
    }
    return 0;
};

export const todayKey = (): string => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${mm}-${dd}`;
};

export const formatDateKeyEs = (dateKey: string): string => {
    const [y, m, d] = dateKey.split("-");
    return `${d}/${m}/${y}`;
};

const formatHour = (hour: number): string => {
    const hh = Math.floor(hour) % 24;
    const mm = Math.floor((hour % 1) * 60);
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

export class AforoSimulator {
    private zones: AforoZone[];

    private runtime: { [zoneId: string]: ZoneRuntime } = {};

    private profileCache: { [key: string]: AforoProfilePoint[] } = {};

    private mode: AforoMode = "snapshot";

    private snapshotDateKey = todayKey();

    private snapshotHour = 12;

    // Instante real (ms) en el que el reloj del replay arrancó.
    private clockStartMs = performance.now();

    constructor(zones: AforoZone[]) {
        this.zones = zones;
    }

    // Zonas con aforo: las ocultas de relleno y las de solo etiqueta no cuentan
    // en gráficas, informes, aforo total ni respuestas del avatar.
    getZones = (areaId?: AforoAreaId): AforoZone[] =>
        this.zones.filter((z) => !z.hidden && !z.soloEtiqueta && (!areaId || z.area === areaId));

    getMode = (): AforoMode => this.mode;

    getSnapshot = (): { dateKey: string; hour: number } => ({
        dateKey: this.snapshotDateKey,
        hour: this.snapshotHour,
    });

    setReplay = (): void => {
        this.mode = "replay";
        this.evac = null;
        this.clockStartMs = performance.now();
    };

    // ---- Evacuación ----
    private evac: { startMs: number; result: EvacuationResult } | null = null;

    /** Simula la evacuación de una planta con la gente de una fecha y hora, sin tocar el plano. */
    previewEvacuation = (areaId: AforoAreaId, dateKey: string, hour: number): EvacuationResult => {
        // En el parking cada coche sale de su plaza: las ocupadas de la foto
        // (solo si es la foto en pantalla, que es de donde sale el reparto).
        const esFotoActual = dateKey === this.snapshotDateKey && hour === this.snapshotHour;
        return simulateEvacuation(
            areaId,
            this.getStatesAt(dateKey, hour, areaId).map((s) => {
                const plazas = this.plazaPos[s.zone.id];
                const starts =
                    plazas && esFotoActual
                        ? plazas.filter((_, k) => this.getSeatStatus(s.zone.id, k) !== "libre")
                        : undefined;
                return { zone: s.zone, personas: s.personas, starts };
            })
        );
    };

    /** Arranca la evacuación de la foto actual sobre el plano (modo "evacuacion"). */
    startEvacuation = (areaId: AforoAreaId): EvacuationResult => {
        const result = this.previewEvacuation(areaId, this.snapshotDateKey, this.snapshotHour);
        this.mode = "evacuacion";
        this.evac = { startMs: performance.now(), result };
        return result;
    };

    /** Vuelve a la foto (la gente reaparece donde estaba). */
    stopEvacuation = (): void => {
        this.evac = null;
        this.mode = "snapshot";
    };

    getEvacuation = (
        nowMs: number = performance.now()
    ): {
        result: EvacuationResult;
        elapsed: number;
        remaining: number;
        finished: boolean;
    } | null => {
        if (!this.evac) return null;
        const { result, startMs } = this.evac;
        const elapsed = Math.min(
            result.duracion,
            ((nowMs - startMs) / 1000) * AFORO_EVACUACION.aceleracion
        );
        const frame = this.evacuationFrame(elapsed);
        return {
            result,
            elapsed,
            remaining: Math.round(frame?.remaining ?? 0),
            finished: elapsed >= result.duracion,
        };
    };

    // Posiciones de los focos del mapa de calor y de las plazas, que registra
    // el visor al generarlos: en una evacuación cada foco vale según la gente
    // que tiene cerca y cada plaza según si su coche ha arrancado, de modo
    // que el mapa sigue exactamente a las personas en movimiento.
    private focos: { [zoneId: string]: Punto[] } = {};

    private plazaPos: { [zoneId: string]: Punto[] } = {};

    registerFocus = (zoneId: string, index: number, p: Punto): void => {
        if (!this.focos[zoneId]) this.focos[zoneId] = [];
        this.focos[zoneId][index] = p;
    };

    registerPlazas = (zoneId: string, plazas: Punto[]): void => {
        this.plazaPos[zoneId] = plazas;
    };

    private evacPositionsCache: { elapsed: number; items: EvacuationPosition[] } | null = null;

    /** Dónde está cada persona de la evacuación en curso (cacheado por fotograma). */
    getEvacuationPositions = (nowMs: number = performance.now()): EvacuationPosition[] => {
        const evac = this.getEvacuation(nowMs);
        if (!evac) {
            this.evacPositionsCache = null;
            return [];
        }
        const elapsed = Math.round(evac.elapsed * 20) / 20;
        if (!this.evacPositionsCache || this.evacPositionsCache.elapsed !== elapsed) {
            this.evacPositionsCache = {
                elapsed,
                items: evacuationPositions(evac.result, elapsed),
            };
        }
        return this.evacPositionsCache.items;
    };

    /** Valor de un foco durante la evacuación (gente cerca / capacidad del foco); null si no hay. */
    getFocusValue = (
        zoneId: string,
        index: number,
        nowMs: number = performance.now()
    ): number | null => {
        if (!this.evac) return null;
        const zone = this.zones.find((z) => z.id === zoneId);
        const focus = this.focos[zoneId]?.[index];
        if (!zone || !focus || zone.area !== this.evac.result.areaId) return null;
        const nFocos = this.focos[zoneId].length || 1;
        const capacidad = Math.max(1, zone.aforoMaximo / nFocos);
        const radio = AFORO_FOCUS_SPACING * 0.75;
        let cerca = 0;
        this.getEvacuationPositions(nowMs).forEach(({ pos }) => {
            if (pos && Math.hypot(pos.x - focus.x, pos.z - focus.z) <= radio) cerca += 1;
        });
        return Math.min(1, cerca / capacidad);
    };

    /** Plaza durante la evacuación: 1 mientras su coche no ha arrancado; null si no hay evacuación. */
    private getPlazaEvacuationValue = (
        zoneId: string,
        plaza: number,
        nowMs: number
    ): number | null => {
        if (!this.evac) return null;
        const zone = this.zones.find((z) => z.id === zoneId);
        const p = this.plazaPos[zoneId]?.[plaza];
        if (!zone || !p || zone.area !== this.evac.result.areaId) return null;
        const elapsed = ((nowMs - this.evac.startMs) / 1000) * AFORO_EVACUACION.aceleracion;
        const ocupada = this.evac.result.gente.some(
            (g) =>
                g.zoneId === zoneId &&
                elapsed < g.tDep &&
                Math.hypot(g.start.x - p.x, g.start.z - p.z) < 150
        );
        return ocupada ? 1 : 0;
    };

    private evacuationFrame = (elapsed: number) => {
        const frames = this.evac?.result.frames;
        if (!frames || frames.length === 0) return null;
        const index = Math.min(frames.length - 1, Math.max(0, Math.round(elapsed * 2)));
        return frames[index];
    };

    // Personas de una zona según la evacuación en curso; null si la zona no
    // participa (otra planta) o no hay evacuación.
    private evacuationTarget = (zoneId: string, nowMs: number): number | null => {
        if (!this.evac) return null;
        const zone = this.zones.find((z) => z.id === zoneId);
        if (!zone || zone.area !== this.evac.result.areaId) return null;
        const elapsed = ((nowMs - this.evac.startMs) / 1000) * AFORO_EVACUACION.aceleracion;
        const frame = this.evacuationFrame(elapsed);
        return frame ? frame.counts[zoneId] ?? 0 : 0;
    };

    // Olvida el estado suavizado: el siguiente fotograma pinta directamente
    // los valores objetivo. Se usa al entrar en una vista para que el heatmap
    // no arranque con los colores de la vista anterior (o un amarillo
    // intermedio) y vaya deslizándose durante unos segundos hasta los reales.
    // Los cambios de hora con el deslizador siguen siendo suaves.
    resetSmoothing = (): void => {
        this.runtime = {};
    };

    setSnapshot = (dateKey: string, hour: number): void => {
        this.mode = "snapshot";
        this.evac = null;
        this.snapshotDateKey = dateKey;
        this.snapshotHour = hour;
    };

    // La fecha seleccionada es futura → los datos son "previsión".
    isForecast = (dateKey: string = this.snapshotDateKey): boolean => dateKey > todayKey();

    // ---- Perfil diario determinista por zona ----
    getDayProfile = (dateKey: string, zoneId: string): AforoProfilePoint[] => {
        const cacheKey = `${dateKey}|${zoneId}`;
        if (this.profileCache[cacheKey]) return this.profileCache[cacheKey];

        const zone = this.zones.find((z) => z.id === zoneId);
        if (!zone) return [];

        const rand = mulberry32(hashSeed(cacheKey));
        const dayOfWeek = new Date(`${dateKey}T12:00:00`).getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const weekendFactor = isWeekend ? 0.12 : 1;
        const dayFactor = 0.75 + rand() * 0.35; // día flojo / día fuerte
        const phase1 = rand() * Math.PI * 2;
        const freq1 = 1.3 + rand() * 1.2;
        const phase2 = rand() * Math.PI * 2;

        const profile: AforoProfilePoint[] = [];
        for (let hour = REPLAY_START_HOUR; hour <= REPLAY_END_HOUR; hour += PROFILE_STEP_HOURS) {
            const base = interpolateCurve(hour) * zone.bias * dayFactor * weekendFactor;
            const wave =
                1 + 0.12 * Math.sin(hour * freq1 + phase1) + 0.07 * Math.sin(hour * 3.7 + phase2);
            const jitter = 0.95 + rand() * 0.1;
            const factor = Math.max(0, Math.min(base * wave * jitter, 1.15));
            const personas = Math.round(zone.aforoMaximo * factor);
            profile.push({ hour, personas, ratio: personas / zone.aforoMaximo });
        }

        // El cache crece con cada día consultado; se poda de vez en cuando.
        if (Object.keys(this.profileCache).length > 400) this.profileCache = {};
        this.profileCache[cacheKey] = profile;
        return profile;
    };

    // Suma de todas las zonas de un área, punto a punto.
    // Perfil del día de una zona con las reservas superpuestas (gráfica e
    // informe; el plano usa lo mismo vía profileValueAt). Sin caché: las
    // reservas cambian.
    getZoneDayProfile = (dateKey: string, zoneId: string): AforoProfilePoint[] => {
        const zone = this.zones.find((z) => z.id === zoneId);
        if (!zone) return [];
        return this.getDayProfile(dateKey, zoneId).map((point) => {
            const reservado = aforoReservations.ocupacionReservada(zoneId, dateKey, point.hour);
            if (reservado <= point.personas) return point;
            return {
                hour: point.hour,
                personas: reservado,
                ratio: zone.aforoMaximo > 0 ? reservado / zone.aforoMaximo : 0,
            };
        });
    };

    // Perfil del día SOLO de reservas (personas que suponen las reservas en
    // cada media hora), de una zona o de toda una planta: para distinguir en
    // la gráfica lo reservado de verdad de lo previsto.
    getReservedDayProfile = (
        dateKey: string,
        areaId: AforoAreaId,
        zoneId?: string
    ): AforoProfilePoint[] => {
        const zones = zoneId ? this.zones.filter((z) => z.id === zoneId) : this.getZones(areaId);
        const aforoTotal = zones.reduce((acc, z) => acc + z.aforoMaximo, 0);
        const profile: AforoProfilePoint[] = [];
        for (let hour = REPLAY_START_HOUR; hour <= REPLAY_END_HOUR; hour += PROFILE_STEP_HOURS) {
            const personas = zones.reduce(
                (acc, z) => acc + aforoReservations.ocupacionReservada(z.id, dateKey, hour),
                0
            );
            profile.push({ hour, personas, ratio: aforoTotal > 0 ? personas / aforoTotal : 0 });
        }
        return profile;
    };

    getAreaDayProfile = (dateKey: string, areaId: AforoAreaId): AforoProfilePoint[] => {
        const zones = this.getZones(areaId);
        const aforoTotal = zones.reduce((acc, z) => acc + z.aforoMaximo, 0);
        const profiles = zones.map((z) => this.getZoneDayProfile(dateKey, z.id));
        if (profiles.length === 0 || profiles[0].length === 0) return [];
        return profiles[0].map((point, i) => {
            const personas = profiles.reduce((acc, p) => acc + (p[i]?.personas ?? 0), 0);
            return {
                hour: point.hour,
                personas,
                ratio: aforoTotal > 0 ? personas / aforoTotal : 0,
            };
        });
    };

    getAforoTotal = (areaId: AforoAreaId): number =>
        this.getZones(areaId).reduce((acc, z) => acc + z.aforoMaximo, 0);

    // Hora "actual" para las consultas en vivo del avatar: la del reloj del
    // navegador (hora local del visitante) o una fija, según AFORO_HORA_EN_VIVO.
    // Fuera del horario simulado (AFORO_HORARIO) los perfiles dan ocupación cero.
    static currentHour = (): number => {
        if (AFORO_HORA_EN_VIVO.modo === "fija") return AFORO_HORA_EN_VIVO.hora;
        const now = new Date();
        return now.getHours() + now.getMinutes() / 60;
    };

    // La misma hora, pero dentro del horario simulado y en pasos de media hora:
    // es la foto inicial del módulo de aforo y del panel de análisis.
    static currentHourInSchedule = (): number => {
        const hour = Math.round(AforoSimulator.currentHour() * 2) / 2;
        return Math.min(REPLAY_END_HOUR, Math.max(REPLAY_START_HOUR, hour));
    };

    // Estado determinista de cada zona en una fecha y hora dadas, sin el
    // suavizado del runtime: es lo que el avatar cuenta al visitante y lo que
    // se pinta al fijar esa misma foto con setSnapshot.
    getStatesAt = (dateKey: string, hour: number, areaId?: AforoAreaId): AforoZoneState[] =>
        this.getZones(areaId).map((zone) => {
            const personas = Math.max(0, Math.round(this.profileValueAt(dateKey, zone.id, hour)));
            return {
                zone,
                personas,
                ratio: zone.aforoMaximo > 0 ? personas / zone.aforoMaximo : 0,
            };
        });

    // Ocupación (sensores o previsión) con las reservas superpuestas: una sala
    // reservada tendrá gente, así que se da por llena en su tramo, y los
    // puestos o plazas reservados se suman. En sentido contrario no: la
    // disponibilidad para reservar solo mira las reservas (aforoReservations).
    private profileValueAt = (dateKey: string, zoneId: string, hour: number): number => {
        const reservado = aforoReservations.ocupacionReservada(zoneId, dateKey, hour);
        return Math.max(this.simulatedValueAt(dateKey, zoneId, hour), reservado);
    };

    private simulatedValueAt = (dateKey: string, zoneId: string, hour: number): number => {
        const profile = this.getDayProfile(dateKey, zoneId);
        if (profile.length === 0) return 0;
        if (hour <= profile[0].hour) return profile[0].personas;
        const last = profile[profile.length - 1];
        if (hour >= last.hour) return last.personas;
        const index = Math.floor((hour - profile[0].hour) / PROFILE_STEP_HOURS);
        const a = profile[index];
        const b = profile[Math.min(index + 1, profile.length - 1)];
        const t = (hour - a.hour) / PROFILE_STEP_HOURS;
        return a.personas + (b.personas - a.personas) * t;
    };

    // Hora simulada del replay (decimal, en bucle).
    getSimHour = (nowMs: number = performance.now()): number => {
        if (this.mode !== "replay") return this.snapshotHour;
        const elapsedHours = ((nowMs - this.clockStartMs) / 3600000) * REPLAY_SPEED;
        const span = REPLAY_END_HOUR - REPLAY_START_HOUR;
        return REPLAY_START_HOUR + (elapsedHours % span);
    };

    // Texto del chip superior del visor.
    getStatusLabel = (nowMs: number = performance.now()): string => {
        if (this.vista === "reservas") {
            const { inicio, fin } = this.reservasSlot;
            return `RESERVAS ${formatDateKeyEs(this.snapshotDateKey)} · ${formatHour(
                inicio
            )}–${formatHour(fin)}`;
        }
        if (this.mode === "replay") {
            return `REPLAY ${formatDateKeyEs(this.snapshotDateKey)} · ${formatHour(
                this.getSimHour(nowMs)
            )}`;
        }
        const evac = this.getEvacuation(nowMs);
        if (evac) {
            const reloj = formatSeconds(evac.elapsed);
            return evac.finished
                ? `EVACUACIÓN TERMINADA · ${reloj}`
                : `EVACUACIÓN · ${reloj} · quedan ${evac.remaining}`;
        }
        const base = `${formatDateKeyEs(this.snapshotDateKey)} · ${formatHour(this.snapshotHour)}`;
        return this.isForecast() ? `${base} · PREVISIÓN` : base;
    };

    getZoneState = (zoneId: string, nowMs: number = performance.now()): AforoZoneState | null => {
        const zone = this.zones.find((z) => z.id === zoneId);
        if (!zone) return null;

        // Objetivo según modo: valor del perfil del día seleccionado, fijo en
        // la hora de la foto o recorrido por el reloj acelerado del replay.
        const evacTarget = this.evacuationTarget(zoneId, nowMs);
        const target =
            evacTarget ?? this.profileValueAt(this.snapshotDateKey, zoneId, this.getSimHour(nowMs));

        let state = this.runtime[zoneId];
        if (state === undefined) {
            state = { current: target, lastMs: nowMs };
            this.runtime[zoneId] = state;
        }

        if (evacTarget !== null) {
            // En una evacuación los contadores van al ritmo de la gente que se
            // mueve, sin suavizado: la cifra de la chip es la de ese instante.
            state.current = target;
        } else {
            // Suavizado exponencial hacia el objetivo, para que los cambios de
            // hora/fecha se vean como una transición fluida y no como un salto.
            const dtSeconds = Math.max(0, (nowMs - state.lastMs) / 1000);
            const alpha = 1 - Math.exp(-dtSeconds / SMOOTHING_SECONDS);
            state.current += (target - state.current) * alpha;
        }
        state.lastMs = nowMs;

        const personas = Math.max(0, Math.round(state.current));
        return { zone, personas, ratio: personas / zone.aforoMaximo };
    };

    getAllZoneStates = (nowMs: number = performance.now()): AforoZoneState[] =>
        this.zones
            .map((zone) => this.getZoneState(zone.id, nowMs))
            .filter((s): s is AforoZoneState => s !== null);

    // Ratio 0..1 para el heatmap (satura en 1 aunque el aforo se supere;
    // el label numérico es el que cuenta la historia del exceso).
    getZoneRatio = (zoneId: string): number => {
        const state = this.getZoneState(zoneId);
        return state ? Math.min(state.ratio, 1) : 0;
    };

    // ---- Asientos: personas simuladas sobre el plano ----
    // El visor registra cuántos asientos (posiciones posibles de persona) ha
    // generado para cada zona. Cada día tiene su propio orden de llenado de
    // esos asientos (barajado determinista por fecha y zona), y a una hora
    // dada están ocupados los `personas` primeros de ese orden. Así, al
    // avanzar la hora la gente aparece y desaparece asiento a asiento, y la
    // misma fecha y hora produce siempre la misma foto.
    private seatCounts: { [zoneId: string]: number } = {};

    private seatOrderCache: { [key: string]: number[] } = {};

    registerSeats = (zoneId: string, count: number): void => {
        this.seatCounts[zoneId] = count;
    };

    // Zonas de paso: la gente no se queda, así que el reparto de asientos
    // cambia cada cuarto de hora simulado (en el replay se ve a la gente
    // moverse por el hall; en una foto, cada hora da una distribución distinta).
    private static TRANSIT_TYPES = new Set(["hall", "acceso", "circulacion"]);

    private seatRanks = (zoneId: string, dateKey: string, hour: number): number[] => {
        const zone = this.zones.find((z) => z.id === zoneId);
        const transit = zone !== undefined && AforoSimulator.TRANSIT_TYPES.has(zone.tipo);
        const slot = transit ? Math.floor(hour * 4) : 0;
        const key = `${dateKey}|${zoneId}|seats|${slot}`;
        const cached = this.seatOrderCache[key];
        if (cached) return cached;
        const count = this.seatCounts[zoneId] ?? 0;
        const order = Array.from({ length: count }, (_, i) => i);
        const rand = mulberry32(hashSeed(key));
        for (let i = order.length - 1; i > 0; i -= 1) {
            const j = Math.floor(rand() * (i + 1));
            [order[i], order[j]] = [order[j], order[i]];
        }
        // ranks[asiento] = posición en el orden de llenado.
        const ranks = new Array<number>(count);
        order.forEach((seat, rank) => {
            ranks[seat] = rank;
        });
        if (Object.keys(this.seatOrderCache).length > 400) this.seatOrderCache = {};
        this.seatOrderCache[key] = ranks;
        return ranks;
    };

    // Valor del sensor de un asiento para el heatmap: 1 si hay una persona,
    // -1 si está vacío (el sombreador ignora los sensores negativos).
    getSeatValue = (zoneId: string, seat: number, nowMs: number = performance.now()): number => {
        const state = this.getZoneState(zoneId, nowMs);
        if (!state || state.personas <= 0) return -1;
        const ranks = this.seatRanks(zoneId, this.snapshotDateKey, this.getSimHour(nowMs));
        const rank = ranks[seat];
        if (rank === undefined) return -1;
        return rank < state.personas ? 1 : -1;
    };

    // Estado de un puesto individual (silla de una zona de trabajo o plaza de
    // parking) en la foto actual. Los puestos reservados son los primeros de
    // la fila (igual que en la vista de reservas) y cuentan como ocupados; el
    // resto de la ocupación simulada se reparte entre los demás según el
    // orden determinista del día.
    getSeatStatus = (
        zoneId: string,
        seat: number,
        nowMs: number = performance.now()
    ): AforoSeatStatus => {
        const state = this.getZoneState(zoneId, nowMs);
        if (!state || state.personas <= 0) return "libre";
        const hour = this.getSimHour(nowMs);
        const reservadas = Math.min(
            state.personas,
            aforoReservations.ocupacionReservada(zoneId, this.snapshotDateKey, hour)
        );
        if (seat < reservadas) return "reservado";
        const ranks = this.seatRanks(zoneId, this.snapshotDateKey, hour);
        if (ranks[seat] === undefined) return "libre";
        const ocupadasPorOrden = ranks
            .map((rank, index) => ({ rank, index }))
            .filter((p) => p.index >= reservadas)
            .sort((a, b) => a.rank - b.rank)
            .slice(0, state.personas - reservadas)
            .map((p) => p.index);
        return ocupadasPorOrden.includes(seat) ? "ocupado" : "libre";
    };

    // Plaza de parking para el heatmap: 1 ocupada (rojo), 0 libre (verde).
    getPlazaValue = (zoneId: string, plaza: number, nowMs: number = performance.now()): number => {
        // En una evacuación la plaza se queda libre cuando su coche arranca.
        const evacuacion = this.getPlazaEvacuationValue(zoneId, plaza, nowMs);
        if (evacuacion !== null) return evacuacion;
        return this.getSeatStatus(zoneId, plaza, nowMs) === "libre" ? 0 : 1;
    };

    // Estado de una zona sumando las que agrupa (p. ej. zona B del parking =
    // sus plazas más las eléctricas), para el chip y las respuestas del avatar.
    getAggregatedState = (
        zoneId: string,
        nowMs: number = performance.now()
    ): AforoZoneState | null => {
        const state = this.getZoneState(zoneId, nowMs);
        if (!state) return null;
        const extra = state.zone.agrupa ?? [];
        if (extra.length === 0) return state;
        let { personas } = state;
        let aforo = state.zone.aforoMaximo;
        extra.forEach((id) => {
            const other = this.getZoneState(id, nowMs);
            if (other) {
                personas += other.personas;
                aforo += other.zone.aforoMaximo;
            }
        });
        return {
            zone: { ...state.zone, aforoMaximo: aforo },
            personas,
            ratio: aforo > 0 ? personas / aforo : 0,
        };
    };

    // ---- Vista de reservas ----
    // Independiente de la ocupación: muestra lo reservado y lo libre de cada
    // espacio reservable en un tramo horario de la fecha seleccionada.
    private vista: AforoVista = "ocupacion";

    private reservasSlot = { inicio: 9, fin: 10 };

    getVista = (): AforoVista => this.vista;

    setVista = (vista: AforoVista): void => {
        this.vista = vista;
        if (vista === "reservas" && this.mode === "replay") {
            // Las reservas se consultan sobre un tramo fijo, no sobre el replay.
            this.mode = "snapshot";
        }
    };

    getReservasSlot = (): { inicio: number; fin: number } => ({ ...this.reservasSlot });

    setReservasSlot = (inicio: number, fin: number): void => {
        if (fin > inicio) this.reservasSlot = { inicio, fin };
    };

    /** Fracción reservada (0..1) de un espacio en el tramo actual; null si no es reservable. */
    getZoneReservedRatio = (zoneId: string): number | null => {
        const { inicio, fin } = this.reservasSlot;
        const d = aforoReservations.disponibilidad(zoneId, this.snapshotDateKey, inicio, fin);
        if (!d) return null;
        return d.capacidad > 0 ? d.reservado / d.capacidad : 0;
    };

    /** Plaza de parking reservada en el tramo actual (las primeras N de la fila). */
    isPlazaReserved = (zoneId: string, plaza: number): boolean => {
        const { inicio, fin } = this.reservasSlot;
        const d = aforoReservations.disponibilidad(zoneId, this.snapshotDateKey, inicio, fin);
        return d !== null && plaza < d.reservado;
    };
}

export type AforoVista = "ocupacion" | "reservas";

export type AforoSeatStatus = "libre" | "ocupado" | "reservado";
